import { useEffect, useMemo, useState } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import useSiteSettings from '@/lib/useSiteSettings';
import PageHero from '@/components/PageHero';
import InlinePlayer from '@/components/InlinePlayer';
import { getVideos } from '@/lib/api';
import PictureImg from '@/components/PictureImg';

const fmt = (s) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

const ALL = 'All films';
// Kept as the safety fallback for the very first paint before
// `useSiteSettings()` resolves. The authoritative list lives in
// `settings.film_page.categories` (CMS-editable).
const DEFAULT_CATEGORIES = ['Openings', 'Artist Interviews', 'Studio Visits', 'Exhibition Walkthroughs', 'Conversations'];

export default function Videos() {
    useDocumentMeta({
        title: 'Film · Maison Touré',
        description: 'A film archive from Maison Touré, Sydney — vernissages, studio visits, artist interviews and curatorial conversations on contemporary African art.',
    });
    const reveal = useReveal();
    const settings = useSiteSettings();
    const hero = settings?.film_page || {};
    const [videos, setVideos] = useState([]);
    const [active, setActive] = useState(ALL);

    useEffect(() => { getVideos().then(setVideos); }, []);

    // Categories come from Site Settings → Film page → Categories.
    // Falls back to the built-in list only if the CMS hasn't seeded yet.
    const categories = useMemo(() => {
        const list = settings?.film_page?.categories;
        const cats = Array.isArray(list) && list.length > 0 ? list : DEFAULT_CATEGORIES;
        return [ALL, ...cats];
    }, [settings]);

    const filtered = useMemo(() => {
        if (active === ALL) return videos;
        return videos.filter((v) => v.category === active);
    }, [videos, active]);

    return (
        <div ref={reveal} data-testid="videos-page" style={{ paddingTop: 144 }}>
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-12 pb-40 md:pb-56">
                <PageHero
                    testid="film-hero"
                    overline={hero.hero_overline ?? 'The Film Archive'}
                    title={hero.hero_title ?? 'Film.'}
                    intro={hero.hero_intro ?? 'A quiet archive of vernissages, studio visits, artist interviews and curatorial conversations — held with the same patience as the works themselves.'}
                />

                <div
                    className="mt-20 md:mt-24 rule-bottom pb-6 flex md:flex-wrap gap-x-10 gap-y-3 overflow-x-auto no-scrollbar"
                    style={{ scrollSnapType: 'x proximity' }}
                >
                    {categories.map((c) => (
                        <button
                            key={c}
                            data-testid={`video-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
                            onClick={() => setActive(c)}
                            className="overline transition-all duration-500 shrink-0"
                            style={{
                                color: active === c ? 'var(--ink)' : 'var(--ink-3)',
                                letterSpacing: active === c ? '0.42em' : '0.34em',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                scrollSnapAlign: 'start',
                            }}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                {filtered.length === 0 && (
                    <p
                        className="mt-32 text-center reveal"
                        style={{
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            fontStyle: 'italic',
                            fontSize: 20,
                            color: 'var(--ink-3)',
                        }}
                    >
                        No films in this section yet.
                    </p>
                )}

                <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-32">
                    {filtered.map((v, i) => (
                        <article
                            key={v.slug}
                            id={v.slug}
                            className={`reveal group ${i % 2 === 1 ? 'md:mt-24' : ''}`}
                            data-testid={`video-card-${v.slug}`}
                        >
                            <InlinePlayer video={v} testid={`video-card-${v.slug}`} />
                            <div className="mt-8">
                                <div className="flex items-baseline justify-between gap-6">
                                    <span className="overline" style={{ color: 'var(--ink-2)' }}>{v.category}</span>
                                    <span
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontStyle: 'italic',
                                            fontSize: 14,
                                            color: 'var(--ink-3)',
                                        }}
                                    >
                                        {fmt(v.published_at)}
                                    </span>
                                </div>
                                <h2
                                    className="mt-5"
                                    style={{
                                        fontFamily: "'Cormorant Garamond', Georgia, serif",
                                        fontStyle: 'italic',
                                        fontWeight: 500,
                                        fontSize: 'clamp(28px, 3vw, 44px)',
                                        lineHeight: 1.12,
                                        letterSpacing: '-0.015em',
                                    }}
                                >
                                    {v.title}
                                </h2>
                                {v.description && (
                                    <p
                                        className="mt-5"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontWeight: 400,
                                            fontSize: 17,
                                            lineHeight: 1.7,
                                            color: 'var(--ink-2)',
                                        }}
                                    >
                                        {v.description}
                                    </p>
                                )}
                                {(v.artist_name || v.exhibition_title) && (
                                    <div
                                        className="mt-5"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontStyle: 'italic',
                                            fontSize: 14,
                                            color: 'var(--ink-3)',
                                        }}
                                    >
                                        {v.artist_name && <>With <em>{v.artist_name}</em></>}
                                        {v.artist_name && v.exhibition_title && ' · '}
                                        {v.exhibition_title && <>{v.exhibition_title}</>}
                                    </div>
                                )}
                            </div>
                        </article>
                    ))}
                </div>
            </section>
        </div>
    );
}

export function VideoThumb({ video }) {
    return (
        <div className="img-wrap aspect-[16/10] relative" style={{ background: 'var(--bg-3)' }}>
            {video.thumbnail && (
                <PictureImg src={video.thumbnail} alt={video.title} loading="lazy" />
            )}
            <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-700"
                style={{ background: 'linear-gradient(180deg, rgba(20,18,15,0.0) 50%, rgba(20,18,15,0.25) 100%)' }}
            >
                <span
                    style={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        border: '1px solid rgba(249,248,246,0.85)',
                        background: 'rgba(20,18,15,0.18)',
                        backdropFilter: 'blur(6px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <span
                        aria-hidden
                        style={{
                            display: 'block',
                            width: 0,
                            height: 0,
                            borderLeft: '12px solid rgba(249,248,246,0.95)',
                            borderTop: '8px solid transparent',
                            borderBottom: '8px solid transparent',
                            marginLeft: 4,
                        }}
                    />
                </span>
            </div>
        </div>
    );
}
